import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import lodash from "lodash";
import * as XLSX from "xlsx";
import DataTable from "react-data-table-component";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { FaRegFileExcel } from "react-icons/fa";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import useStyles from "./dashboardInsights.style";
import dashboardInsightsAPI from "../../../../api/adminAPI/dashboardInsightsAPI";
import { getTimeZone, getUserData, localTimeZone } from "../../../../common";

const RANGES = [
  { value: "THIS_MONTH", label: "This month" },
  { value: "LAST_3_MONTHS", label: "Last 3 months" },
  { value: "LAST_12_MONTHS", label: "Last 12 months" },
  { value: "CUSTOM", label: "Custom" },
];

const DATE_FORMAT = "YYYY-MM-DD";

const presetDates = (range) => {
  const today = moment();
  switch (range) {
    case "THIS_MONTH":
      return { from: today.clone().startOf("month"), to: today };
    case "LAST_3_MONTHS":
      return {
        from: today.clone().subtract(3, "months").add(1, "day"),
        to: today,
      };
    default:
      return {
        from: today.clone().subtract(12, "months").add(1, "day"),
        to: today,
      };
  }
};

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// auctions saved without a currency come back as ""; a select can't hold ""
// as a real option, so it gets its own key
const NO_CURRENCY = "__NONE__";
const currencyKey = (currency) => currency || NO_CURRENCY;
const currencyLabel = (currency) => currency || "No currency";

const formatValues = (values) =>
  lodash.isEmpty(values)
    ? "-"
    : values
        .map(({ currency, value }) => `${formatNumber(value)} ${currency}`)
        .join(", ");

const monthLabel = (period) => moment(period, "YYYY-MM").format("MMM YY");
const dayLabel = (period) => moment(period, DATE_FORMAT).format("DD MMM");

function StatCard({ label, value, hint }) {
  const classes = useStyles();
  return (
    <div className="col-md-3 col-6">
      <div className={classes.statCard}>
        <Typography className={classes.statLabel}>{label}</Typography>
        <Typography className={classes.statValue}>{value}</Typography>
        {hint && <Typography className={classes.statHint}>{hint}</Typography>}
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  const classes = useStyles();
  return (
    <div className="col-lg-4 col-md-6">
      <div className={classes.chartCard}>
        <Typography className={classes.chartTitle}>{title}</Typography>
        <div className={classes.chartBody}>{children}</div>
      </div>
    </div>
  );
}

function DashboardInsights() {
  const classes = useStyles();
  const userData = getUserData() ?? {};
  const timeZone = getTimeZone() || localTimeZone();

  const [range, setRange] = useState("LAST_12_MONTHS");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [insights, setInsights] = useState(null);
  const [apiState, setApiState] = useState("loading");
  const [valueCurrency, setValueCurrency] = useState("");

  const dates = useMemo(() => {
    if (range !== "CUSTOM") {
      const { from, to } = presetDates(range);
      return { from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) };
    }
    return { from: customFrom, to: customTo };
  }, [range, customFrom, customTo]);

  useEffect(() => {
    // wait until both custom dates are picked
    if (!dates.from || !dates.to) return;

    let ignore = false;
    const getInsights = async () => {
      setApiState("loading");
      try {
        const { data } = await dashboardInsightsAPI({ ...dates, timeZone });
        if (ignore) return;
        setInsights(data);
        setApiState("success");
      } catch (error) {
        if (!ignore) setApiState("error");
      }
    };
    getInsights();

    return () => {
      ignore = true;
    };
  }, [dates, timeZone]);

  const leadValueByMonth = insights?.trends?.leadValueByMonth ?? [];
  const currencies = lodash.map(leadValueByMonth, "currency");
  // keep the chosen currency while it is still in the data, else show the first
  const selectedCurrency = currencies.includes(valueCurrency)
    ? valueCurrency
    : currencies[0];
  const valueMonths =
    lodash.find(leadValueByMonth, { currency: selectedCurrency })?.months ?? [];

  const participation = insights?.participation;
  const trends = insights?.trends;
  const sellerSummary = insights?.sellerSummary;

  const sellerColumns = [
    { name: "Seller", selector: (row) => row.seller, sortable: true, grow: 2 },
    { name: "Auctions", selector: (row) => row.auctions, sortable: true },
    { name: "Lots", selector: (row) => row.lots, sortable: true },
    { name: "Invited Buyers", selector: (row) => row.invited, sortable: true },
    {
      name: "Active Bidders",
      selector: (row) => row.activeBidders,
      sortable: true,
    },
    { name: "Bids", selector: (row) => row.bids, sortable: true },
    {
      name: "Lead Bid Value",
      selector: (row) => formatValues(row.leadValue),
      grow: 2,
    },
  ];

  const exportSellerSummary = () => {
    const header = lodash.map(sellerColumns, "name");
    const rows = lodash.map(sellerSummary, (row) => [
      row.seller,
      row.auctions,
      row.lots,
      row.invited,
      row.activeBidders,
      row.bids,
      formatValues(row.leadValue),
    ]);

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...rows]);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sellers");
    XLSX.writeFile(
      workbook,
      `seller-summary-${insights.range.from}-to-${insights.range.to}.xlsx`
    );
  };

  return (
    <div className={classes.insights}>
      <div className={classes.filterBar}>
        <Typography className={classes.sectionTitle}>Insights</Typography>
        <Box className={classes.filters}>
          <TextField
            select
            size="small"
            label="Period"
            sx={{ minWidth: 170 }}
            value={range}
            onChange={(event) => setRange(event.target.value)}
          >
            {RANGES.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          {range === "CUSTOM" && (
            <>
              <TextField
                type="date"
                size="small"
                label="From"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: customTo || undefined }}
                value={customFrom}
                onChange={(event) => setCustomFrom(event.target.value)}
              />
              <TextField
                type="date"
                size="small"
                label="To"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: customFrom || undefined }}
                value={customTo}
                onChange={(event) => setCustomTo(event.target.value)}
              />
            </>
          )}
          {apiState === "loading" && <CircularProgress size={22} />}
        </Box>
      </div>

      {range === "CUSTOM" && (!customFrom || !customTo) && (
        <Typography className={classes.message}>
          Pick a From and To date to see the insights.
        </Typography>
      )}
      {apiState === "error" && (
        <Typography className={classes.message}>
          Could not load the insights. Please try again.
        </Typography>
      )}

      {participation && (
        <>
          <Typography className={classes.headText}>
            Bidder participation
          </Typography>
          <div className="row">
            <StatCard
              label="Total Buyers"
              value={formatNumber(participation.totalBuyers)}
              hint={
                userData.role === "SELLER"
                  ? "Added to your auctions"
                  : "Registered and active"
              }
            />
            <StatCard
              label="Active Bidders"
              value={formatNumber(participation.activeBidders)}
              hint="Placed a bid in this period"
            />
            <StatCard
              label="New Buyers"
              value={formatNumber(participation.newBuyers)}
              hint={
                userData.role === "SELLER"
                  ? "First added in this period"
                  : "Registered in this period"
              }
            />
            <StatCard
              label="Participation"
              value={`${participation.participationPct}%`}
              hint={`${formatNumber(
                participation.invitedWhoBid
              )} of ${formatNumber(participation.invited)} invited bid`}
            />
          </div>

          <div className={classes.tableCard}>
            <Typography className={classes.chartTitle}>
              Top 5 bidders
            </Typography>
            {lodash.isEmpty(participation.topBidders) ? (
              <Typography className={classes.message}>
                No bids in this period.
              </Typography>
            ) : (
              <div className={classes.tableScroll}>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th>Bidder</th>
                      <th>Auctions</th>
                      <th>Bids</th>
                      <th>Lead Lots</th>
                      <th>Lead Bid Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participation.topBidders.map((bidder) => (
                      <tr key={bidder.userId}>
                        <td>
                          <div className={classes.bidderName}>
                            {bidder.name || "-"}
                          </div>
                          <div className={classes.bidderEmail}>
                            {bidder.email}
                          </div>
                        </td>
                        <td>{formatNumber(bidder.auctions)}</td>
                        <td>{formatNumber(bidder.bids)}</td>
                        <td>{formatNumber(bidder.leadLots)}</td>
                        <td>{formatValues(bidder.leadValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {trends && (
        <>
          <Typography className={classes.headText}>Trends</Typography>
          <div className="row">
            <ChartCard title="Auctions per month">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trends.auctionsByMonth}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="period"
                    tickFormatter={monthLabel}
                    fontSize={11}
                  />
                  <YAxis allowDecimals={false} fontSize={11} width={32} />
                  <Tooltip labelFormatter={monthLabel} />
                  <Bar
                    dataKey="auctions"
                    name="Auctions"
                    fill="#2D5DD7"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title={
                trends.bidsBucket === "week" ? "Bids per week" : "Bids per day"
              }
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trends.bidsByPeriod}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="period"
                    tickFormatter={dayLabel}
                    fontSize={11}
                    minTickGap={16}
                  />
                  <YAxis allowDecimals={false} fontSize={11} width={32} />
                  <Tooltip
                    labelFormatter={(period) =>
                      trends.bidsBucket === "week"
                        ? `Week of ${dayLabel(period)}`
                        : dayLabel(period)
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="bids"
                    name="Bids"
                    stroke="#07AD00"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Lead bid value per month">
              {lodash.isEmpty(currencies) ? (
                <Typography className={classes.message}>
                  No bids in this period.
                </Typography>
              ) : (
                <>
                  {currencies.length > 1 && (
                    <TextField
                      select
                      size="small"
                      label="Currency"
                      className={classes.currencySelect}
                      value={currencyKey(selectedCurrency)}
                      onChange={(event) =>
                        setValueCurrency(
                          event.target.value === NO_CURRENCY
                            ? ""
                            : event.target.value
                        )
                      }
                    >
                      {currencies.map((currency) => (
                        <MenuItem
                          key={currencyKey(currency)}
                          value={currencyKey(currency)}
                        >
                          {currencyLabel(currency)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  <ResponsiveContainer
                    width="100%"
                    height={currencies.length > 1 ? "80%" : "100%"}
                  >
                    <BarChart data={valueMonths}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="period"
                        tickFormatter={monthLabel}
                        fontSize={11}
                      />
                      <YAxis
                        fontSize={11}
                        width={56}
                        tickFormatter={(value) =>
                          Number(value).toLocaleString("en-IN", {
                            notation: "compact",
                          })
                        }
                      />
                      <Tooltip
                        labelFormatter={monthLabel}
                        formatter={(value) => [
                          `${formatNumber(value)} ${selectedCurrency}`,
                          "Lead bid value",
                        ]}
                      />
                      <Bar
                        dataKey="value"
                        fill="#FFC400"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </>
              )}
            </ChartCard>
          </div>
        </>
      )}

      {sellerSummary && (
        <div className={classes.tableCard}>
          <div className={classes.tableHeader}>
            <Typography className={classes.chartTitle}>
              Seller-wise summary
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={exportSellerSummary}
              disabled={lodash.isEmpty(sellerSummary)}
              startIcon={<FaRegFileExcel style={{ color: "#04aa18" }} />}
            >
              Export
            </Button>
          </div>
          <DataTable
            columns={sellerColumns}
            data={sellerSummary}
            keyField="userId"
            pagination
            highlightOnHover
            dense
            noDataComponent="No auctions in this period."
          />
        </div>
      )}
    </div>
  );
}

export default DashboardInsights;
